'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

type QA = {
  id: string;
  question: string;
  answer: string;
  sources: string[];
};

const predefinedQA: Record<string, QA> = {
  'passport': {
    id: 'qa-001',
    question: 'What documents are required for Malaysian passport renewal?',
    answer: 'For Malaysian passport renewal, citizens require: (1) Current passport (original), (2) MyKad (original and photocopy), (3) Completed application form, (4) Payment of RM200 (5-year) or RM300 (10-year), and (5) One passport-size photograph with white background (35mm × 50mm). Processing typically takes 3–5 working days. Same-day express service is available at selected offices.',
    sources: ['Passport Renewal Requirements', 'Immigration Service Guide'],
  },
  'mysejahtera': {
    id: 'qa-002',
    question: 'How do I update my mobile number in MySejahtera?',
    answer: 'To update your MySejahtera mobile number: Navigate to Profile Settings → Personal Information → Update Mobile Number. An OTP will be sent to your current registered number for verification. If you have lost access to your registered number, you must use the Account Recovery procedure or visit the nearest Klinik Kesihatan with your MyKad.',
    sources: ['How to Update MySejahtera Mobile Number', 'MySejahtera Account Recovery Procedure'],
  },
  'str': {
    id: 'qa-003',
    question: 'Who is eligible for STR assistance?',
    answer: 'STR (Sumbangan Tunai Rahmah) eligibility is based on household income thresholds. Malaysian citizens with household income below RM5,000/month may be eligible. Applicants must register through the official STR portal with their MyKad and provide income documentation. Eligibility is verified against LHDN and EPF records automatically.',
    sources: ['STR Application — Eligibility and Requirements', 'Government Assistance Portal Guide'],
  },
};

const suggestedQuestions = [
  'What documents are required for Malaysian passport renewal?',
  'How do I update my mobile number in MySejahtera?',
  'Who is eligible for STR assistance?',
  'How do I renew road tax online?',
];

export default function AIAskPanel() {
  const [query, setQuery] = useState('');
  const [history, setHistory] = useState<QA[]>([]);
  const [loading, setLoading] = useState(false);

  const handleAsk = (q?: string) => {
    const question = q || query;
    if (!question.trim()) return;

    setLoading(true);
    setQuery('');

    // Backend integration point: replace with /api/ai/knowledge-search
    setTimeout(() => {
      const lower = question.toLowerCase();
      let result: QA;
      if (lower.includes('passport') || lower.includes('renew')) {
        result = { ...predefinedQA['passport'], id: `qa-${Date.now()}`, question };
      } else if (lower.includes('mysejahtera') || lower.includes('mobile')) {
        result = { ...predefinedQA['mysejahtera'], id: `qa-${Date.now()}`, question };
      } else if (lower.includes('str') || lower.includes('assistance')) {
        result = { ...predefinedQA['str'], id: `qa-${Date.now()}`, question };
      } else {
        result = {
          id: `qa-${Date.now()}`,
          question,
          answer: 'Based on available knowledge sources, I found relevant information. Please refer to the specific agency knowledge articles for detailed guidance. For complex cases, escalate to the relevant agency through the standard escalation procedure.',
          sources: ['MyGCC General Service Guide', 'Agency Contact Directory'],
        };
      }
      setHistory((prev) => [result, ...prev]);
      setLoading(false);
    }, 1800);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-ai-border bg-blue-50/60 flex-shrink-0">
        <Icon name="SparklesIcon" size={15} className="text-primary" />
        <h3 className="text-[13px] font-700 text-primary">Ask CX AI</h3>
        <span className="text-[10px] bg-primary text-white px-1.5 py-0.5 rounded font-600">AI</span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-4">
        {history.length === 0 && !loading && (
          <div>
            <div className="text-[12px] text-muted-foreground mb-3">Ask a question about Government services and CX AI will search the verified knowledge base.</div>
            <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Suggested Questions</div>
            <div className="space-y-1.5">
              {suggestedQuestions.map((q, i) => (
                <button
                  key={`sq-${i}`}
                  onClick={() => handleAsk(q)}
                  className="w-full text-left text-[12px] bg-white border border-border rounded px-3 py-2 hover:bg-secondary hover:border-primary/30 transition-colors text-foreground font-500"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center py-6 space-y-2">
            <div className="flex gap-1">
              {[0, 1, 2].map((i) => (
                <span key={`ld-${i}`} className="w-2 h-2 bg-primary rounded-full ai-listening-dot" style={{ animationDelay: `${i * 0.16}s` }} />
              ))}
            </div>
            <div className="text-[12px] text-primary font-600">Searching knowledge base...</div>
          </div>
        )}

        {history.map((item) => (
          <div key={item.id} className="space-y-2 fade-in-up">
            {/* Question */}
            <div className="flex justify-end">
              <div className="bg-primary text-white text-[12px] rounded-lg px-3 py-2 max-w-[85%] leading-relaxed">
                {item.question}
              </div>
            </div>
            {/* Answer */}
            <div className="bg-white border border-border rounded-lg px-3 py-2.5">
              <p className="text-[12px] text-foreground leading-relaxed mb-2.5">{item.answer}</p>
              <div className="border-t border-border pt-2">
                <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground mb-1.5">
                  Based on {item.sources.length} verified knowledge source{item.sources.length > 1 ? 's' : ''}
                </div>
                {item.sources.map((s, si) => (
                  <div key={`src-${item.id}-${si}`} className="flex items-center gap-1.5 text-[11px] text-primary mb-1">
                    <Icon name="DocumentTextIcon" size={10} className="flex-shrink-0" />
                    <span className="font-500 hover:underline cursor-pointer">{s}</span>
                  </div>
                ))}
                <div className="text-[10px] text-amber-600 mt-1.5 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={10} />
                  AI-generated · Verify with official sources · Not policy advice
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="p-3 border-t border-ai-border bg-card flex-shrink-0">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Ask about Government services..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAsk()}
            className="flex-1 px-3 py-2 text-[12px] border border-border rounded bg-background focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <button
            onClick={() => handleAsk()}
            disabled={!query.trim() || loading}
            className="btn-primary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="PaperAirplaneIcon" size={14} />
          </button>
        </div>
        <div className="text-[10px] text-muted-foreground mt-1.5 text-center">
          Responses grounded in verified Government knowledge · Human review recommended
        </div>
      </div>
    </div>
  );
}